import { Cell } from '../../components/table/types';

import type {
  CitedValue,
  ScreenedProject,
  ScreeningAttribute,
  ScreeningDetailsEnvelope,
} from './types';

const formatScreeningRating = (start?: string, end?: string) => {
  if (!start && !end) return undefined;
  if (!end || start === end) return start;
  if (!start) return end;
  return `${start}-${end}`;
};

export const RATING_SCALE = ['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'C', 'D'] as const;

// No rating at all and a range spanning the whole scale carry the same meaning
// to a reader: the model could not place the project.
export const isNoAssessment = (start?: string, end?: string) =>
  !start || !end || (start === RATING_SCALE[0] && end === RATING_SCALE[RATING_SCALE.length - 1]);

const flattenCited = (v: unknown): string | undefined => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && 'value' in v) {
    const val = (v as { value: unknown }).value;
    if (typeof val === 'string') return val;
  }
  return undefined;
};

const findAttributeValue = (attrs: ScreeningAttribute[] | undefined, title: string) =>
  attrs?.find((a) => a.title?.toLowerCase() === title.toLowerCase())?.value;

export const buildScreeningPayloadFromEnvelope = (envelope: ScreeningDetailsEnvelope) => {
  const { result } = envelope;
  const methodologies = result?.methodologies;
  return {
    project_id: envelope.project_id,
    project_name: result?.name,
    project_type: result?.projectType?.display_name,
    methodology:
      flattenCited(result?.methodology) ?? findAttributeValue(result?.attributes, 'methodology'),
    est_rating: formatScreeningRating(result?.rating?.start, result?.rating?.end),
    additionality_risk: methodologies?.additionality?.riskLevel,
    permanence_risk: methodologies?.permanence?.riskLevel,
    overcrediting_risk: methodologies?.overCrediting?.riskLevel,
    overlapping_claims_risk: methodologies?.overlappingClaims?.riskLevel,
    info_gaps: result?.infoGaps?.impactLevel,
  };
};

const SCREENING_COMPLETED_ANALYTICS_KEYS = [
  'project_id',
  'project_name',
  'project_type',
  'methodology',
  'est_rating',
  'additionality_risk',
  'permanence_risk',
  'overcrediting_risk',
  'overlapping_claims_risk',
  'info_gaps',
] as const;

const toAnalyticsValue = (v: unknown, depth = 2): string | number | boolean | undefined => {
  if (v == null) return undefined;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  if (depth > 0 && typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    if (typeof obj.display_name === 'string') return obj.display_name;
    if ('value' in obj) return toAnalyticsValue(obj.value, depth - 1);
  }
  return undefined;
};

export const pickScreeningCompletedFields = (
  resultObj: Record<string, unknown> | null | undefined
) => {
  if (!resultObj) return {};
  const picked: Record<string, unknown> = {};

  const set = (key: (typeof SCREENING_COMPLETED_ANALYTICS_KEYS)[number], value: unknown) => {
    if (picked[key] !== undefined) return;
    const v = toAnalyticsValue(value);
    if (v !== undefined) picked[key] = v;
  };

  for (const key of SCREENING_COMPLETED_ANALYTICS_KEYS) {
    set(key, resultObj[key]);
  }

  const nested = resultObj.result as Record<string, unknown> | undefined;
  const attrs = nested?.attribute_extraction as Record<string, { value?: unknown }> | undefined;
  const report = nested?.report as Record<string, unknown> | undefined;
  const riskCards = report?.risk_cards as Record<string, { risk_level?: unknown }> | undefined;
  const infoGaps = report?.information_gaps as { level?: unknown } | undefined;

  set('project_id', attrs?.project_id?.value);
  set('methodology', attrs?.methodology_id?.value);
  set('est_rating', report?.rating);
  set('additionality_risk', riskCards?.additionality?.risk_level);
  set('permanence_risk', riskCards?.permanence?.risk_level);
  set('overcrediting_risk', riskCards?.over_crediting?.risk_level);
  set('overlapping_claims_risk', riskCards?.overlapping_claims?.risk_level);
  set('info_gaps', infoGaps?.level);

  return picked;
};

export const buildScreeningPayloadFromRow = (row: ScreenedProject) => {
  const projectType = row.projectType as ScreenedProject['projectType'] | { display_name?: string };
  return {
    project_id: row.projectId,
    project_name: row.name,
    project_type:
      typeof projectType === 'string'
        ? projectType
        : (projectType as { display_name?: string })?.display_name,
    methodology: row.methodology,
    est_rating: formatScreeningRating(row.rating?.start, row.rating?.end),
    info_gaps: row.infoGap,
  };
};

export const formatEtaLabel = (etaSeconds: number | null): string | null => {
  if (etaSeconds == null) return null;
  const minutes = Math.round(Math.max(0, etaSeconds) / 60);
  if (minutes < 1) return 'Estimated time remaining: less than a minute';
  return `Estimated time remaining: ${minutes} minute${minutes === 1 ? '' : 's'}`;
};

export const citedValue = (field: CitedValue | string | undefined): string => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field.value ?? '';
};

export interface SourcePart {
  file: string;
  /** "page 3", "page 2 and 5" or "pages 2, 4 and 9". */
  pages: string;
}

/** The files a value was cited from, in citation order, each with its pages. */
export const sourcePartsFromCitations = (field: CitedValue | undefined): SourcePart[] => {
  const fileOrder: string[] = [];
  const pagesByFile = new Map<string, number[]>();
  for (const c of field?.citations ?? []) {
    if (!c?.page) continue;
    const fileName = c.source_file?.split('/').pop() || 'PDD';
    let pages = pagesByFile.get(fileName);
    if (!pages) {
      pages = [];
      pagesByFile.set(fileName, pages);
      fileOrder.push(fileName);
    }
    if (!pages.includes(c.page)) pages.push(c.page);
  }

  const formatPages = (pages: number[]) => {
    if (pages.length === 1) return `page ${pages[0]}`;
    if (pages.length === 2) return `page ${pages[0]} and ${pages[1]}`;
    return `pages ${pages.slice(0, -1).join(', ')} and ${pages[pages.length - 1]}`;
  };

  return fileOrder.map((file) => ({ file, pages: formatPages(pagesByFile.get(file) ?? []) }));
};

export type RiskLevel =
  | 'High risk'
  | 'Low risk'
  | 'Low-med to medium risk'
  | 'Not enough information';

export const riskLevelCompassKey: Record<RiskLevel, string> = {
  'High risk': 'high',
  'Low risk': 'low',
  'Low-med to medium risk': 'low_to_medium',
  'Not enough information': 'none',
};

const RISK_TOKEN_LABEL: Record<string, string> = {
  LOW: 'Low',
  'LOW-MEDIUM': 'Low-med',
  LOW_MEDIUM: 'Low-med',
  MEDIUM: 'Med',
  'MEDIUM-HIGH': 'Med-high',
  MEDIUM_HIGH: 'Med-high',
  HIGH: 'High',
  'NO-RISK': 'No',
  NONE: 'No',
};

export const riskTokenPretty = (raw: string): string | null => {
  const t = raw.trim().toUpperCase().replace(/\s+/g, '-').replace(/_/g, '-');
  return RISK_TOKEN_LABEL[t] ?? null;
};

export const riskLevelDisplayLabel = (level?: string | null): string => {
  if (!level) return 'Not enough info';
  const trimmed = String(level).trim();
  if (!trimmed) return 'Not enough info';
  const upper = trimmed.toUpperCase();
  if (
    upper === 'INSUFFICIENT_DATA' ||
    upper === 'NOT ENOUGH INFO' ||
    upper === 'NOT ENOUGH INFORMATION'
  ) {
    return 'Not enough info';
  }
  if (upper === 'NO_RISK' || upper === 'NO RISK') return 'No risk';

  // Range form "X to Y" — capitalize start, lowercase end (Figma rule).
  const parts = trimmed.split(/\s+to\s+/i);
  if (parts.length === 2) {
    const start = riskTokenPretty(parts[0]);
    const end = riskTokenPretty(parts[1]);
    if (start && end) return `${start} to ${end.toLowerCase()} risk`;
  }
  const single = riskTokenPretty(trimmed);
  if (single) return `${single} risk`;
  return trimmed;
};

export const infoGapDisplayLabel = (level?: string | null): string => {
  if (!level) return 'Not enough info';
  const upper = String(level).trim().toUpperCase();
  if (upper === 'LOW') return 'Low';
  if (upper === 'MEDIUM' || upper === 'MODERATE') return 'Moderate';
  if (upper === 'HIGH') return 'High';
  if (upper === 'CRITICAL') return 'Critical';
  return 'Not enough info';
};

export const normalizeRiskLevelKey = (level?: string | null): string => {
  if (!level) return 'none';
  const raw = String(level).trim().toLowerCase();
  if (!raw || raw === 'not enough info' || raw === 'not enough information') return 'none';
  // "No risk" / "None" reuses the low-risk green palette per Figma (compass/risk/low).
  if (raw === 'none' || /^no[\s_-]?risk$/.test(raw)) return 'low';

  const hasHigh = /high/.test(raw);
  const hasMedium = /medium|moderate/.test(raw);
  const hasLowMedium = /low[\s-]*medium|low to medium/.test(raw);
  const hasLow = /low/.test(raw);

  // Most-specific first.
  if (raw === 'critical') return 'high';
  if (hasMedium && hasHigh) return 'medium_to_high';
  if (hasLowMedium || (hasLow && hasMedium)) return 'low_to_medium';
  if (hasHigh) return 'high';
  if (hasMedium) return 'medium';
  if (hasLow) return 'low';
  return 'none';
};

// `compassKey` indexes getRiskLevelColorCodeRange; `paletteKey` indexes
// theme.compass, which the results pill reads from the light palette so the
// swatch stays light in both colour schemes.
const MEDIA_COVERAGE_CHIPS = {
  positive: { label: 'Positive coverage', compassKey: 'low', paletteKey: 'low' },
  mixed: { label: 'Mixed coverage', compassKey: 'medium', paletteKey: 'medium' },
  negative: { label: 'Negative coverage', compassKey: 'high', paletteKey: 'high' },
  none: {
    label: 'No relevant coverage identified',
    compassKey: 'low_to_medium',
    paletteKey: 'low-medium',
  },
} as const;

/**
 * `esr_findings.media_screening_rating` is free-form and has shipped in three
 * vocabularies — Favorable/Unfavorable, Negative media found/No negative media
 * found, and the current Mixed coverage — all of which are still live in the
 * data. Matching is exact rather than substring so "No negative media found"
 * can't be read as negative and "Unfavorable" can't be read as favorable.
 */
const MEDIA_COVERAGE_BY_VALUE: Record<string, keyof typeof MEDIA_COVERAGE_CHIPS> = {
  favorable: 'positive',
  favourable: 'positive',
  'positive coverage': 'positive',
  mixed: 'mixed',
  'mixed coverage': 'mixed',
  unfavorable: 'negative',
  unfavourable: 'negative',
  'negative coverage': 'negative',
  'negative media found': 'negative',
  'no negative media found': 'none',
  'no relevant coverage': 'none',
  // The screener now emits the chip label itself as the rating value.
  'no relevant coverage identified': 'none',
  none: 'none',
};

/** Figma 4363:86613. Unrecognised or empty values render no chip, never a guess. */
export const mediaScreeningChip = (level?: string | null) => {
  const key =
    MEDIA_COVERAGE_BY_VALUE[
      String(level ?? '')
        .trim()
        .toLowerCase()
    ];
  return key ? MEDIA_COVERAGE_CHIPS[key] : null;
};

export type InfoGapLevel = 'Low' | 'Moderate' | 'High' | 'Critical' | 'Not enough info';

export const infoGapCompassKey: Record<string, string> = {
  low: 'low',
  moderate: 'medium',
  medium: 'medium',
  'medium-high': 'medium_to_high',
  medium_to_high: 'medium_to_high',
  high: 'medium_to_high',
  critical: 'high',
  'Not enough info': 'none',
  'not enough info': 'none',
};

export const SUPPORTED_PROJECT_TYPES = [
  'Afforestation and reforestation',
  'Cookstoves',
  'Landfill gas',
  'Improved forest management',
  'Mine methane',
  'Other',
] as const;

export const SCREENABLE_PROJECT_TYPES: ReadonlyArray<{
  name: string;
  short?: string;
  glyph?: string;
}> = [
  {
    name: 'Afforestation and reforestation',
    short: 'AR',
    glyph: 'public/img/glyph/REDD_Avoided_Deforestation.svg',
  },
  { name: 'Cookstoves', glyph: 'public/img/glyph/Improved_Cookstoves.svg' },
  { name: 'Landfill gas', glyph: 'public/img/glyph/Landfill_Gas.svg' },
  {
    name: 'Ozone depleting substances destruction',
    glyph: 'public/img/glyph/Industrial_gas_destruction.svg',
  },
];

export const formatScreenableProjectTypesList = (): string => {
  const labels = SCREENABLE_PROJECT_TYPES.map((t) => (t.short ? `${t.name} (${t.short})` : t.name));
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

// Mirrors the 5 BE status_codes (DOCS_PROCESSED → … → SCREENING_REPORT).
export const ANALYSIS_STEPS = [
  'Reading your documents',
  'Identifying project type and methodology',
  'Assessing GHG risks',
  'Organizing key findings',
  'Drafting your screening report',
] as const;

export const ACCEPTED_FORMATS = '.pdf,.docx';

export type FaqStats = { ratingsCount: number; methodologiesCount: number };

const floorTo = (value: number, step: number) => Math.floor(value / step) * step;

export const getFaqItems = (
  stats?: FaqStats
): ReadonlyArray<{ question: string; answer: ReadonlyArray<string> }> => {
  const ratingsLabel = stats
    ? `over ${floorTo(stats.ratingsCount, 10).toLocaleString()}`
    : 'over 1,100';
  // const methodologiesLabel = stats ? `${floorTo(stats.methodologiesCount, 10)}+` : '80+';
  const methodologiesLabel = '80+';

  return [
    {
      question: 'How does SmartScan work?',
      answer: [
        `SmartScan analyzes the project documentation you upload and returns an estimated GHG rating range and an assessment of the key risks and red flags for the project within minutes. It draws on insights from Calyx Global's catalog of ${ratingsLabel} expert ratings across ${methodologiesLabel} methodologies to identify the risk drivers most relevant to your project type and methodology. These are the same risk factors that underpin our GHG ratings methodology — additionality, permanence, over-crediting, and overlapping claims.`,
        'SmartScan does not replicate the full depth of a Calyx Global rating. It focuses on the factors that carry the most weight for a given project type. Use it to quickly prioritize projects, identify the risks most worth investigating, and focus conversations with project developers on what matters most.',
      ],
    },
    {
      question: 'How is a SmartScan result different from a Calyx Global rating?',
      answer: [
        'A Calyx Global rating is a comprehensive assessment conducted by our team of experts. It draws on a range of information including the published version of the PDD, monitoring and verification reports, and additional data and information our research team has investigated, e.g. from academic studies and government data. It produces a specific letter-grade rating after deep investigation by our analysts on all aspects of a project.',
        'By contrast, a SmartScan result is an AI-generated analysis based on the documents you upload. It analyzes those documents through the lens of selected elements of our rating framework that matter most for your project type and methodology. For critical elements, project information is weighed against benchmarks derived from the same independent sources used in our ratings. SmartScan produces a rating range and a summary of key risks and red flags — not a complete analysis of every factor our team reviews. It is designed for speed and prioritization, not as a substitute for a formal rating.',
        'A SmartScan result reflects the information available at the time of screening. For early-stage projects, that may mean a draft PDD. The screening result will reflect the assumptions in that draft. If those assumptions change in a later version and you re-run the screening, the result may change.',
      ],
    },
    {
      question: 'How should I interpret the estimated rating range?',
      answer: [
        "The estimated range reflects the model's assessment of the most material integrity risks, based on your uploaded documents and the key risk factors that the model is designed to assess. In our internal testing on projects we have already rated, the model's estimated range was accurate in over 90% of cases, i.e. the final (human) rating fell within the AI-generated estimated ratings range.",
        "That said, carbon projects are often complex and highly specific. Treat the range as a well-informed starting point, not a guarantee. The model can make mistakes, and the range may also shift if documentation, registry data, the project's operational plans, or reported outcomes differ from what was available at the time of screening. More complete documentation generally produces a more accurate estimate.",
      ],
    },
    {
      question: 'Why did my screening return a wide rating range?',
      answer: [
        'A wide range usually reflects genuine uncertainty driven by, for example, key project attributes that are missing or ambiguous. In such cases, the model returns a broader range rather than forcing a false precision.',
        'A wide range is also useful information: it tells you where to focus. The screening report identifies the factors driving the uncertainty. Uploading more complete documentation — such as a recent monitoring or verification report alongside the PDD — will often narrow the range.',
      ],
    },
    {
      question: 'What documents should I upload for the best results?',
      answer: [
        'Upload the Project Design Document (PDD) and the most recent Monitoring or Verification Report (if available). Together, these give the model the most complete picture of the project.',
        'If your documents are updated or you want to test a revised version, you can run a new screening at any time. Each new screening session uses one Screening credit.',
      ],
    },
    {
      question: 'How is my uploaded data handled?',
      answer: [
        'We do not use your uploaded documents to train AI models. On occasion, authorized Calyx Global personnel may review uploaded documents and the corresponding output to troubleshoot errors and refine how our system processes them.',
        'Screening reports are accessible within the platform to all members of your organization with access to the Calyx Global platform.',
      ],
    },
    {
      question: 'Can I download or share the screening report?',
      answer: [
        'Screening reports can be downloaded and shared within your organization.',
        'We appreciate that sharing this information with others may be valuable to you. Sharing a report outside of your organization requires approval from Calyx Global. To request approval, contact us here. This allows us to ensure any external use of the report includes appropriate context about what it is and how it should be interpreted.',
      ],
    },
    {
      question:
        'How many Screening credits does it take to analyze multiple documents for the same project?',
      answer: [
        'One Screening credit covers a single analysis session, regardless of how many documents you upload in that session. If you upload a PDD and a monitoring report together, SmartScan analyzes them as a combined set and uses one credit.',
        'Running a second session for the same project — for example, after uploading updated documentation — uses an additional credit.',
      ],
    },
  ];
};

export const finePrintItems = [
  {
    title: 'What happens after I upload my files?',
    description:
      "We'll review your evidence to estimate a rating range and assess GHG and media risks. Screening results will be visible to other people in your organization.",
  },
  {
    title: 'Assessment quality is influenced by uploaded information.',
    description:
      'This assessment relies entirely on the information and assumptions provided in your document. Incorrect or incomplete information will produce incorrect results.',
  },
  {
    title: 'Assessments are powered by AI',
    description:
      'Our AI model uses extensive data from our expert ratings to generate insights. While highly reliable, we recommend treating these findings as a robust starting point rather than the final word, as AI can still make mistakes.',
  },
];

// `rating` and `download_report` are virtual columns rendered via customCell.
const LEFT_ALIGN = {
  textAlign: 'start' as const,
  justifyContent: 'flex-start' as const,
  fontWeight: 400,
  fontSize: 'sm' as const,
  ml: 0,
};

const CENTER_ALIGN = {
  textAlign: 'center' as const,
  justifyContent: 'center' as const,
  fontWeight: 400,
  fontSize: 'sm' as const,
  ml: 0,
};

export const columns: Cell[][] = [
  [
    {
      key: 'name',
      name: 'Project name',
      isPinned: true,
      isSortable: true,
      cellStyle: {
        width: '256px',
        position: 'sticky',
        left: 0,
        zIndex: 11,
        borderRight: '2px solid var(--TableCell-borderColor)',
      },
      contentStyle: { ...LEFT_ALIGN, textWrap: 'wrap' },
    },
    {
      key: 'createdAt',
      name: 'Date screened',
      isSortable: true,
      cellStyle: { width: '109px' },
      contentStyle: { ...LEFT_ALIGN, textWrap: 'wrap' },
    },
    {
      key: 'projectId',
      name: 'Project ID',
      isSortable: true,
      cellStyle: { width: '104px' },
      contentStyle: LEFT_ALIGN,
    },
    {
      key: 'infoGap',
      name: 'Information gaps',
      isSortable: true,
      cellStyle: { width: '153px' },
      contentStyle: { ...LEFT_ALIGN, textWrap: 'wrap' },
      tooltip:
        'Screening accuracy depends on available information. If necessary details are missing, we flag it as an information gap.',
    },
    {
      key: 'rating',
      name: 'Estimated GHG rating range',
      isSortable: true,
      cellStyle: { width: '153px' },
      contentStyle: { ...CENTER_ALIGN, textWrap: 'wrap' },
    },
    {
      key: 'riskLevel',
      name: 'Media screening',
      cellStyle: { width: '168px' },
      contentStyle: { ...LEFT_ALIGN, textWrap: 'wrap' },
    },
    {
      key: 'download_report',
      name: 'Download report',
      cellStyle: { width: '97px' },
      contentStyle: { ...CENTER_ALIGN, textWrap: 'wrap' },
    },
    {
      key: 'projectType',
      name: 'Project type',
      isSortable: true,
      cellStyle: { width: '176px' },
      contentStyle: LEFT_ALIGN,
    },
    {
      key: 'country',
      name: 'Country',
      isSortable: true,
      cellStyle: { width: '112px' },
      contentStyle: { ...LEFT_ALIGN, textWrap: 'wrap', overflowWrap: 'anywhere' },
    },
    {
      key: 'methodology',
      name: 'Methodology',
      isSortable: true,
      cellStyle: { width: '232px' },
      contentStyle: { ...LEFT_ALIGN, textWrap: 'wrap' },
    },
    {
      key: 'registry',
      name: 'Registry',
      isSortable: true,
      cellStyle: { width: '134px' },
      contentStyle: { ...LEFT_ALIGN, textWrap: 'wrap' },
    },
    {
      key: 'proponent',
      name: 'Proponent',
      isSortable: true,
      cellStyle: { width: '400px' },
      contentStyle: { ...LEFT_ALIGN, textWrap: 'wrap' },
    },
  ],
];

export const SCREENER_DEFAULT_SORT: Record<string, 'ASC' | 'DESC'> = {
  default: 'ASC',
  rating: 'DESC',
};

export const cellRenderOrder: string[] = [
  'name',
  'createdAt',
  'projectId',
  'infoGap',
  'rating',
  'riskLevel',
  'download_report',
  'projectType',
  'country',
  'methodology',
  'registry',
  'proponent',
];

const STUB_LOREM = 'Lorem ipsum dolor sit amet';

/** Locked-state preview rows shown behind the upgrade overlay (no API). */
export const STUB_SAMPLE_PROJECTS: Array<Record<string, string>> = Array.from(
  { length: 5 },
  (_, idx) => ({
    id: String(idx + 1),
    name: `${STUB_LOREM} consectetur adipiscing elit`,
    createdAt: 'Lorem ipsum',
    projectId: 'Lorem ipsum',
    infoGap: 'Lorem',
    rating: 'Lorem ipsum',
    riskLevel: 'Lorem ipsum',
    projectType: 'Lorem ipsum dolor',
    country: 'Lorem ipsum',
    methodology: 'Lorem ipsum dolor sit',
    registry: 'Lorem ipsum',
    proponent: 'Lorem ipsum',
  })
);
